import type {
  PathLink as SitumPathLink,
  PathNode as SitumPathNode,
  Paths as SitumPaths,
} from "@situm/sdk-js";

import { generateLinkIDFromNodes } from "@/features/editor/components/paths/utils";
import type { Building } from "@/libs/models";

/****************************************************************************/
/* Types */
/****************************************************************************/

export enum DefaultLinkTags {
  SOUND = "sound",
  CROWD = "crowd",
  LIGHT = "light",
  STEEP = "steep",
  UNEVEN = "uneven",
}

type PathsNodeProperties = SitumPathNode & {
  lat: number;
  lng: number;
};
type PathsLinkProperties = SitumPathLink;

export type PathDictNodeItem = Omit<PathsNodeProperties, "id"> & {
  id: string;
  type: "node";
  metadata?: {
    attachedLinks?: Record<string, { targetFloor: number | null }>;
  };
};
export type PathDictLinkItem = Omit<
  PathsLinkProperties,
  "source" | "target"
> & {
  id: string;
  type: "link";
  floorId: number | null;
  source: string;
  target: string;
};
export type PathDictGenericItem = PathDictNodeItem | PathDictLinkItem;
export type PathDict = Record<string, PathDictGenericItem>;

export type PathDictNodeItemForVisualization = PathDictNodeItem & {
  z?: number;
};
export type PathDictLinkItemForVisualization = PathDictLinkItem & {
  from: { lat: number; lng: number; z?: number };
  to: { lat: number; lng: number; z?: number };
  tagType?: "included" | "excluded";
};
type FloorChangeLink = PathDictLinkItemForVisualization & {
  from: { lat: number; lng: number; z: number };
  to: { lat: number; lng: number; z: number };
  fromFloorId: number | null;
  toFloorId: number | null;
};

/**
 * Processed data to be rendered by the PathsLayer
 */
export type PathsDataForVisualization = {
  normalNodes: PathDictNodeItemForVisualization[];
  normalLinks: PathDictLinkItemForVisualization[];
  notAccessibleLinks: PathDictLinkItemForVisualization[];
  floorChangeLinks: FloorChangeLink[];
  floorChangeFromNodes: PathDictNodeItemForVisualization[];
  floorChangeToNodes: PathDictNodeItemForVisualization[];
};

/****************************************************************************/
/* Transformations */
/****************************************************************************/

export const pathsToPathDict = (building: Building) => {
  if (!building?.paths) return {};

  const nodes =
    building.paths.nodes?.reduce(
      (acc, item) => {
        // Ignore calculated lat lng in map viewer's model.ts, and recalculate
        // them from the x-y position with the appropriate coordinate converter
        const coordinate = building.coordinateConverterFlatEarth.toCoordinate({
          x: item.x,
          y: item.y,
        });

        acc[item.id.toString()] = {
          ...item,
          id: item.id.toString(),
          lat: coordinate.lat,
          lng: coordinate.lng,
          type: "node",
        };
        return acc;
      },
      {} as Record<string, PathDictNodeItem>,
    ) ?? {};

  const links =
    building.paths.links?.reduce(
      (acc, item) => {
        const id = generateLinkIDFromNodes(
          item.source.toString(),
          item.target.toString(),
        );
        const sourceFloorID = (nodes[item.source] as PathDictNodeItem).floorId;
        const targetFloorID = (nodes[item.target] as PathDictNodeItem).floorId;
        acc[id] = {
          ...item,
          floorId: sourceFloorID == targetFloorID ? sourceFloorID : null,
          id,
          source: item.source.toString(),
          tags: item.tags ?? [], // Ensure tags is at least empty array
          target: item.target.toString(),
          type: "link",
        };
        return acc;
      },
      {} as Record<string, PathDictLinkItem>,
    ) ?? {};

  return {
    ...nodes,
    ...links,
  } as PathDict;
};

export const pathDictToPaths = (building: Building, pathDict?: PathDict) => {
  if (!pathDict || !building) return;
  const pathItems = Object.values(pathDict).reduce(
    (acc: { nodes: PathDictNodeItem[]; links: PathDictLinkItem[] }, item) => {
      if (item.type == "link") {
        acc.links.push(item);
      } else if (item.type == "node") {
        const cartesian = building.coordinateConverterFlatEarth.toCartesian({
          lat: item.lat,
          lng: item.lng,
        });
        acc.nodes.push({ ...item, x: cartesian.x, y: cartesian.y });
      }
      return acc;
    },
    { links: [], nodes: [] },
  );

  const nodes: SitumPathNode[] = pathItems.nodes.map((node) => ({
    ...node,
    id: parseInt(node.id),
  }));
  const links: SitumPathLink[] = pathItems.links.map((link) => ({
    ...link,
    source: parseInt(link.source),
    target: parseInt(link.target),
  }));

  return new Paths({ links, nodes });
};

/****************************************************************************/
/* Classes */
/****************************************************************************/

export type PathLink = PathsLinkProperties;
export type PathNode = PathsNodeProperties;

type PathsProperties = {
  nodes: PathsNodeProperties[];
  links: PathsLinkProperties[];
};
export class Paths implements PathsProperties {
  nodes: PathNode[];
  links: PathLink[];

  constructor(paths: SitumPaths) {
    Object.assign(this, paths);
  }
}
